/**
 * BuyerPublicProfilePanel — buyer-facing self-edit for the info that
 * sellers will see in their drawer (`/seller/buyers` → click any row).
 *
 * Shipped Jun 2026 alongside the seller drawer's new "Company overview"
 * block. Buyer fills these in once; sellers see them on every
 * connection / invite acceptance / terms decision.
 *
 * 7 self-reported fields:
 *   • Founded year                  (any 4-digit year between 1800–today)
 *   • Website                       (clickable URL on seller side)
 *   • Industry / category chips     (multi-select, max 10)
 *   • Social handles                (LinkedIn / Instagram / Twitter)
 *   • Store/brand type              (one of 7 enum values)
 *   • Annual revenue band           (3 buckets · banded, not exact)
 *   • Geographic reach              (single / regional / national / international)
 *
 * Drives a profile-completeness nudge: as more fields fill in, sellers
 * see a richer card → faster terms decisions for the buyer.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Eye, Sparkles, ExternalLink, Plus, X, Building2, Globe,
  Linkedin, Instagram, Hash, MapPin, TrendingUp, Store,
} from "lucide-react";
import api from "../lib/api";
import { FieldRow, SectionHeader } from "./SettingsShell";
import { Button } from "./ui/button";
import { Input } from "./ui/input";


const STORE_TYPES = [
  { v: "boutique",     l: "Boutique" },
  { v: "specialty",    l: "Specialty retailer" },
  { v: "dept_store",   l: "Department store" },
  { v: "chain",        l: "Chain" },
  { v: "online_only",  l: "Online-only" },
  { v: "distributor",  l: "Wholesale distributor" },
  { v: "spa_pro",      l: "Spa / professional services" },
];
const REVENUE_BANDS = [
  { v: "under_500k", l: "Under $500K" },
  { v: "500k_5m",    l: "$500K – $5M" },
  { v: "5m_plus",    l: "$5M+" },
];
const GEO_REACH = [
  { v: "single",        l: "Single location" },
  { v: "regional",      l: "Regional" },
  { v: "national",      l: "National" },
  { v: "international", l: "International" },
];
const SUGGESTED_CATEGORIES = [
  "Skincare", "Cosmetics", "Body care", "Wellness", "Apothecary",
  "Fragrance", "Hair care", "Men's grooming", "Baby & kids",
  "Spa supplies", "Home goods", "Apparel", "Food & beverage",
];


function Pill({ active, onClick, children, testId }) {
  return (
    <button type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-[12.5px] px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-foreground text-background border-foreground"
          : "bg-background border-foreground/15 text-foreground/65 hover:border-foreground/40"
      }`}
    >
      {children}
    </button>
  );
}


export default function BuyerPublicProfilePanel() {
  const [form, setForm] = useState({
    founded_year: "",
    website: "",
    categories: [],
    social: { linkedin: "", instagram: "", twitter: "" },
    store_type: "",
    revenue_band: "",
    geographic_reach: "",
  });
  const [catInput, setCatInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setForm(prev => ({
          ...prev,
          founded_year:     data.founded_year != null ? String(data.founded_year) : "",
          website:          data.website || "",
          categories:       Array.isArray(data.categories) ? data.categories : [],
          social: {
            linkedin:  (data.social || {}).linkedin  || "",
            instagram: (data.social || {}).instagram || "",
            twitter:   (data.social || {}).twitter   || "",
          },
          store_type:       data.store_type || "",
          revenue_band:     data.revenue_band || "",
          geographic_reach: data.geographic_reach || "",
        }));
      } catch { /* keep defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  // Profile-completeness % drives the nudge banner. Each of the 7
  // sections counts equally; categories needs ≥1 entry to score.
  const completeness = useMemo(() => {
    let filled = 0;
    if (form.founded_year) filled++;
    if (form.website) filled++;
    if (form.categories.length > 0) filled++;
    if (form.social.linkedin || form.social.instagram || form.social.twitter) filled++;
    if (form.store_type) filled++;
    if (form.revenue_band) filled++;
    if (form.geographic_reach) filled++;
    return Math.round((filled / 7) * 100);
  }, [form]);

  const addCategory = (c) => {
    const clean = (c || "").trim();
    if (!clean) return;
    if (form.categories.includes(clean)) return;
    if (form.categories.length >= 10) {
      toast.error("Max 10 categories — remove one first");
      return;
    }
    setForm({ ...form, categories: [...form.categories, clean] });
    setCatInput("");
  };
  const removeCategory = (c) => {
    setForm({ ...form, categories: form.categories.filter(x => x !== c) });
  };

  const save = async () => {
    setSaving(true);
    try {
      // Strip empty strings from social before sending.
      const social = Object.fromEntries(
        Object.entries(form.social).filter(([, v]) => (v || "").trim()),
      );
      await api.patch("/profile", {
        founded_year: form.founded_year ? Number(form.founded_year) : null,
        website:      form.website?.trim() || null,
        categories:   form.categories,
        social,
        store_type:       form.store_type || null,
        revenue_band:     form.revenue_band || null,
        geographic_reach: form.geographic_reach || null,
      });
      toast.success("Profile saved · sellers see this");
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-[13px] text-foreground/55">Loading…</div>;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Public profile"
        desc="What sellers see about your business when they invite you or consider extending terms. All fields are optional — but the more you fill in, the easier it is for sellers to trust you."
      />

      {/* Completeness nudge */}
      <div
        data-testid="buyer-profile-completeness"
        className={`rounded-xl border p-4 flex items-center gap-3 ${
          completeness === 100
            ? "bg-emerald-50 border-emerald-200"
            : completeness >= 60
              ? "bg-secondary/40 border-border"
              : "bg-amber-50 border-amber-200"
        }`}
      >
        <div className="relative w-12 h-12 shrink-0">
          <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
            <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="3"/>
            <circle cx="18" cy="18" r="16" fill="none" stroke="currentColor"
              strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${(completeness / 100) * 100.5} 100.5`}/>
          </svg>
          <div className="absolute inset-0 grid place-items-center mono text-[11px] font-semibold tabular-nums">
            {completeness}%
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-medium">
            {completeness === 100
              ? "Profile complete — looking sharp"
              : completeness >= 60
                ? "Your profile is taking shape"
                : "Sellers see this — completed profiles get faster term approvals"}
          </div>
          <div className="text-[11.5px] text-foreground/60 mt-0.5">
            Sellers viewing your card see only what you filled in. Empty fields are hidden, not shown as blanks.
          </div>
        </div>
      </div>

      {/* Identity row */}
      <FieldRow label="Founded year" hint="The year your business was established. Doesn't need to match your verqo signup date.">
        <Input
          type="number" min={1800} max={new Date().getFullYear()}
          value={form.founded_year}
          onChange={e => setForm({ ...form, founded_year: e.target.value })}
          placeholder="e.g. 2019"
          data-testid="bpp-founded-year"
          className="h-10 w-32 mono"
        />
      </FieldRow>

      <FieldRow label="Website" hint="Your storefront, marketing site, or About page.">
        <Input
          type="url"
          value={form.website}
          onChange={e => setForm({ ...form, website: e.target.value })}
          placeholder="https://yourbrand.com"
          data-testid="bpp-website"
          className="h-10 max-w-md"
        />
      </FieldRow>

      <FieldRow label="Industry / categories" hint="Which categories you buy in. Sellers use this to gauge brand fit. Max 10.">
        <div className="space-y-2 max-w-md">
          <div className="flex items-center gap-1.5 flex-wrap">
            {form.categories.map(c => (
              <span key={c} data-testid={`bpp-cat-${c}`}
                className="chip bg-background border border-foreground/15 inline-flex items-center gap-1 text-[12px]">
                {c}
                <button type="button" onClick={() => removeCategory(c)}
                  className="hover:text-destructive">
                  <X className="w-2.5 h-2.5"/>
                </button>
              </span>
            ))}
            {form.categories.length === 0 && (
              <span className="text-[11.5px] text-foreground/45">No categories yet</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Input
              value={catInput}
              onChange={e => setCatInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addCategory(catInput); } }}
              placeholder="Add a category — e.g. Skincare"
              data-testid="bpp-cat-input"
              className="h-10 flex-1"
            />
            <Button type="button" variant="outline" onClick={() => addCategory(catInput)}
              data-testid="bpp-cat-add" className="rounded-full">
              <Plus className="w-3.5 h-3.5 mr-1"/>Add
            </Button>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            {SUGGESTED_CATEGORIES.filter(c => !form.categories.includes(c)).slice(0, 8).map(c => (
              <button type="button" key={c} onClick={() => addCategory(c)}
                data-testid={`bpp-cat-suggest-${c}`}
                className="text-[11px] text-foreground/55 hover:text-foreground underline">
                + {c}
              </button>
            ))}
          </div>
        </div>
      </FieldRow>

      {/* Social handles */}
      <FieldRow label="Social handles" hint="Soft-vet signals. Linked profiles open in a new tab on seller side.">
        <div className="space-y-2 max-w-md">
          <div className="flex items-center gap-2">
            <Linkedin className="w-3.5 h-3.5 text-foreground/55 shrink-0"/>
            <Input
              value={form.social.linkedin}
              onChange={e => setForm({ ...form, social: { ...form.social, linkedin: e.target.value } })}
              placeholder="linkedin.com/company/yourname"
              data-testid="bpp-social-linkedin"
              className="h-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Instagram className="w-3.5 h-3.5 text-foreground/55 shrink-0"/>
            <Input
              value={form.social.instagram}
              onChange={e => setForm({ ...form, social: { ...form.social, instagram: e.target.value } })}
              placeholder="@yourbrand"
              data-testid="bpp-social-instagram"
              className="h-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Hash className="w-3.5 h-3.5 text-foreground/55 shrink-0"/>
            <Input
              value={form.social.twitter}
              onChange={e => setForm({ ...form, social: { ...form.social, twitter: e.target.value } })}
              placeholder="@yourbrand (X / Twitter)"
              data-testid="bpp-social-twitter"
              className="h-10"
            />
          </div>
        </div>
      </FieldRow>

      {/* Brand fit signals */}
      <FieldRow label="Store / brand type" hint="What kind of business you are. Sellers use this for brand-fit decisions.">
        <div className="flex flex-wrap gap-1.5 max-w-2xl">
          {STORE_TYPES.map(opt => (
            <Pill key={opt.v}
              active={form.store_type === opt.v}
              onClick={() => setForm({ ...form, store_type: form.store_type === opt.v ? "" : opt.v })}
              testId={`bpp-store-${opt.v}`}>
              {opt.l}
            </Pill>
          ))}
        </div>
      </FieldRow>

      <FieldRow label="Annual revenue (estimated)" hint="A rough band — never an exact number. Helps sellers calibrate order-size expectations.">
        <div className="flex flex-wrap gap-1.5">
          {REVENUE_BANDS.map(opt => (
            <Pill key={opt.v}
              active={form.revenue_band === opt.v}
              onClick={() => setForm({ ...form, revenue_band: form.revenue_band === opt.v ? "" : opt.v })}
              testId={`bpp-revenue-${opt.v}`}>
              {opt.l}
            </Pill>
          ))}
        </div>
      </FieldRow>

      <FieldRow label="Geographic reach" hint="Where you sell. Single location, regional, national, or international.">
        <div className="flex flex-wrap gap-1.5">
          {GEO_REACH.map(opt => (
            <Pill key={opt.v}
              active={form.geographic_reach === opt.v}
              onClick={() => setForm({ ...form, geographic_reach: form.geographic_reach === opt.v ? "" : opt.v })}
              testId={`bpp-reach-${opt.v}`}>
              {opt.l}
            </Pill>
          ))}
        </div>
      </FieldRow>

      {/* Save button + live preview */}
      <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
        <div className="text-[11.5px] text-foreground/55 inline-flex items-center gap-1.5">
          <Eye className="w-3 h-3"/>
          Sellers see this on their <code className="bg-secondary/50 px-1 py-0.5 rounded text-[10.5px] mono">/seller/buyers</code> drawer.
        </div>
        <Button onClick={save} disabled={saving}
          data-testid="bpp-save"
          className="rounded-full bg-foreground text-background">
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
    </div>
  );
}
